import React, { useEffect, useState } from 'react';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import PropTypes from 'prop-types';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Button from 'components/Button';
import Callout from 'components/Callout';
import { CharactersRemaining } from 'components/Form';
import { Input } from 'components/HookForm';
import Message from 'components/Message';
import { openModal } from 'components/Modal';
import Prompt from 'components/Prompt';
import { FooterBar } from 'components/SaveBar';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';

import IntegratedPartnerBanner from 'features/Sku/Items/components/IntegratedPartnerBanner';
import { SKU_NAME_MAX_INPUT_LENGTH } from 'features/Sku/Items/SkuItems.constants';

import CustomerPreview from './CustomerPreview';
import RulesBlock, { RULES_BLOCK_MODE } from './RulesBlock';
import {
  EDIT_SHARED_GROUP_MODAL_NAME,
  GroupAvailabilityDropdown,
  ModifierItemPicker,
  SortableOptionRow,
} from './components';
import { blankOption, toFormValues, toOptionViews } from './Modifiers.formState';
import { sharedEditNeedsConfirm, unsatisfiableRule } from './Modifiers.saveGuards';
import useModifierGroupSave, { SAVE_FAILURE } from './useModifierGroupSave';
import useModifiersReadOnly from './useModifiersReadOnly';
import useOptionReorder from './useOptionReorder';
import useRuleError from './useRuleError';
import messages from './ModifierGroupEditor.messages';
import './ModifierGroupEditor.scss';

const { block, element } = bem('modifier-group-editor');

export const MODIFIER_EDITOR_MODE = RULES_BLOCK_MODE;

const NAME_COUNTER_THRESHOLD = 10;

export const MODIFIER_FORM_ID = 'modifier-group-editor-form';

const SAVE_LABELS = {
  [MODIFIER_EDITOR_MODE.CREATE]: messages.save,
  [MODIFIER_EDITOR_MODE.EDIT]: messages.saveEdit,
};

const ModifierGroupEditor = ({ venueId, mode, group, seed, items, onCancel, onSaved }) => {
  const isReadOnly = mode === MODIFIER_EDITOR_MODE.READ_ONLY;
  const dispatch = useDispatch();
  const intl = useIntl();
  const { integrationName } = useModifiersReadOnly();
  const [hasSaved, setHasSaved] = useState(false);
  const [savedGroupId, setSavedGroupId] = useState(null);
  const methods = useForm({ defaultValues: seed || toFormValues(group) });
  const { fields, append, move, remove } = useFieldArray({ control: methods.control, name: 'modifiers' });
  const values = useWatch({ control: methods.control, defaultValue: methods.getValues() });
  const optionViews = toOptionViews(values);
  const { sensors, handleDragEnd } = useOptionReorder({ fields, move });

  const { save, saveFailed } = useModifierGroupSave({
    venueId,
    group,
    adoptSavedOption: (clientId, modifierId) => {
      const row = (methods.getValues().modifiers || []).findIndex(option => option.core?.id === clientId);
      if (row >= 0) methods.setValue(`modifiers.${row}.core.id`, modifierId);
    },
    onSaved: groupId => {
      // Without this, the unsaved-changes blocker stops its own success path: the form is still
      // dirty here, so saving prompts about discarding and the redirect never runs.
      setHasSaved(true);
      methods.reset(methods.getValues());
      setSavedGroupId(groupId);
    },
  });

  const { setServerRefused } = useRuleError({
    methods,
    values,
    message: intl.formatMessage(messages.ruleBlocked),
  });

  const runSave = async formValues => {
    setServerRefused((await save(formValues)) === SAVE_FAILURE.RULE);
  };

  // Redirecting straight from the save callback unmounts this tree while react-hook-form and
  // react-query are mid-way through notifying their subscribers, so everything still queued behind
  // us writes to dead components. Leaving the current task lets both finish before the route moves.
  useEffect(() => {
    if (savedGroupId === null) return undefined;
    const redirect = setTimeout(() => onSaved(savedGroupId), 0);
    return () => clearTimeout(redirect);
  }, [savedGroupId, onSaved]);

  const handleSave = formValues => {
    if (isReadOnly) return undefined;

    setServerRefused(false);

    if (unsatisfiableRule(formValues)) return undefined;

    if (sharedEditNeedsConfirm(group, formValues)) {
      dispatch(
        openModal(EDIT_SHARED_GROUP_MODAL_NAME, {
          groupName: (formValues.name || '').trim(),
          itemNames: (group.attachedItems || []).map(item => item.name).filter(Boolean),
          onConfirmSave: () => runSave(formValues),
        })
      );
      return undefined;
    }

    return runSave(formValues);
  };

  const groupName = values.name || '';
  const previewItemName = items.find(item => (values.itemIds || []).includes(item.id))?.name;

  return (
    <FormProvider {...methods}>
      <Prompt when={methods.formState.isDirty && !isReadOnly && !hasSaved} message={messages.unsavedChanges} />
      <form id={MODIFIER_FORM_ID} className={block()} onSubmit={methods.handleSubmit(handleSave)} noValidate>
        <When predicate={isReadOnly}>
          <div className={element('integrated-banner')}>
            <IntegratedPartnerBanner integrationName={integrationName} />
          </div>
        </When>

        <When predicate={saveFailed}>
          <Callout className={element('save-error')} variant="error">
            <Message as="text" message={messages.saveFailed} />
          </Callout>
        </When>

        <div className={element('layout')}>
          <div className={element('column')}>
            <When predicate={mode !== MODIFIER_EDITOR_MODE.CREATE}>
              <div className={element('availability')}>
                <GroupAvailabilityDropdown name="availabilityStatus" disabled={isReadOnly} />
              </div>
            </When>

            <section className={element('section')}>
              <h3 className={element('section-heading')}>
                <Message message={messages.detailsHeading} />
              </h3>
              <div>
                <Input
                  name="name"
                  label={messages.nameLabel}
                  placeholder={messages.namePlaceholder}
                  maxLength={SKU_NAME_MAX_INPUT_LENGTH}
                  required={messages.nameRequired}
                  showRequiredLabel
                  disabled={isReadOnly}
                  noMargin
                />
                <When predicate={SKU_NAME_MAX_INPUT_LENGTH - groupName.length <= NAME_COUNTER_THRESHOLD}>
                  <div className={element('name-counter', { error: groupName.length >= SKU_NAME_MAX_INPUT_LENGTH })}>
                    <CharactersRemaining length={SKU_NAME_MAX_INPUT_LENGTH} value={groupName} showMaxLength />
                  </div>
                </When>
              </div>
              <ModifierItemPicker name="itemIds" items={items} disabled={isReadOnly} />
            </section>

            <section className={element('section')}>
              <h3 className={element('section-heading')}>
                <Message message={messages.optionsHeading} />
              </h3>
              <p className={element('section-subtitle')}>
                <Message message={messages.optionsSubtitle} />
              </p>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={fields.map(field => field.id)} strategy={verticalListSortingStrategy}>
                  <div className={element('option-rows')}>
                    {fields.map((field, index) => (
                      <SortableOptionRow
                        key={field.id}
                        sortableId={field.id}
                        fieldName={`modifiers.${index}`}
                        arrayName="modifiers"
                        onRemove={() => remove(index)}
                        disabled={isReadOnly}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <When predicate={!isReadOnly}>
                <Button styled="tertiary" className={element('add-option')} onClick={() => append(blankOption())}>
                  <Isvg src={ICONS.plus} className={element('add-option-icon')} />
                  <Message message={messages.addOption} />
                </Button>
              </When>
            </section>

            {/* RulesBlock renders its own card, so wrapping it in a section would double the border. */}
            <RulesBlock mode={mode} name="rules" options={optionViews} />
          </div>

          <aside className={element('preview')}>
            <CustomerPreview
              groupName={groupName}
              itemName={previewItemName}
              options={optionViews}
              rules={values.rules}
            />
          </aside>
        </div>
      </form>

      <FooterBar className={element('footer')} visible>
        <Button styled="tertiary" className={element('footer-cancel')} onClick={onCancel}>
          <Message message={isReadOnly ? messages.close : messages.cancel} />
        </Button>
        <When predicate={!isReadOnly}>
          <Button
            styled="primary"
            className={element('footer-save')}
            type="submit"
            form={MODIFIER_FORM_ID}
            waiting={methods.formState.isSubmitting}
            disabled={methods.formState.isSubmitting}>
            <Message message={SAVE_LABELS[mode] || messages.save} />
          </Button>
        </When>
      </FooterBar>
    </FormProvider>
  );
};

ModifierGroupEditor.propTypes = {
  group: PropTypes.shape({
    attachedItems: PropTypes.arrayOf(PropTypes.shape({ itemId: PropTypes.string })),
    core: PropTypes.shape({ id: PropTypes.string }),
    modifiers: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  items: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
  mode: PropTypes.oneOf(Object.values(MODIFIER_EDITOR_MODE)),
  onCancel: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  seed: PropTypes.shape({
    name: PropTypes.string,
    itemIds: PropTypes.arrayOf(PropTypes.string),
    modifiers: PropTypes.arrayOf(PropTypes.shape({})),
    rules: PropTypes.shape({}),
  }),
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

ModifierGroupEditor.defaultProps = {
  items: [],
  mode: MODIFIER_EDITOR_MODE.CREATE,
  seed: null,
};

export default ModifierGroupEditor;
